import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getRecurringBookingsApi, createRecurringBookingApi, updateRecurringBookingApi, regenerateRecurringBookingApi, deleteRecurringBookingSeriesApi, getCourtsApi, cleanupOrphanedRecurringApi, getCoachingSessionsApi } from '../lib/api';
import { formatHour, getTodayPHT } from '../lib/format';
import { ArrowLeft, Plus, Repeat, Pause, Play, Trash2, RefreshCw, Calendar, Clock, MapPin, User, Mail, Phone, GraduationCap, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const WEEKDAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 18 }, (_, i) => i + 6); // 6 AM to 11 PM
const DURATIONS = Array.from({ length: 12 }, (_, i) => i + 1);

export default function RecurringBookings() {
  const navigate = useNavigate();
  const { user, loading: authLoading, hasPermission } = useAuth();
  const [series, setSeries] = useState([]);
  const [courts, setCourts] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form state
  const [formBookingType, setFormBookingType] = useState('court'); // 'court' | 'coaching' | 'training'
  const [formCoachId, setFormCoachId] = useState('');
  const [formPlayerName, setFormPlayerName] = useState('');
  const [formPlayerEmail, setFormPlayerEmail] = useState('');
  const [formPlayerPhone, setFormPlayerPhone] = useState('');
  const [formCourtId, setFormCourtId] = useState('');
  const [formStartHour, setFormStartHour] = useState('');
  const [formDuration, setFormDuration] = useState('1');
  const [formWeekdays, setFormWeekdays] = useState([]);
  const [formStartDate, setFormStartDate] = useState(() => getTodayPHT());
  const [formInterval, setFormInterval] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    loadData();
  }, [user, authLoading, navigate]);

  const loadData = async () => {
    try {
      const [seriesRes, courtsRes, coachesRes] = await Promise.all([
        getRecurringBookingsApi(),
        getCourtsApi(),
        getCoachingSessionsApi().catch(() => ({ data: [] })),
      ]);
      setSeries(seriesRes.data);
      setCourts(courtsRes.data);
      setCoaches(coachesRes.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormBookingType('court');
    setFormCoachId('');
    setFormPlayerName('');
    setFormPlayerEmail('');
    setFormPlayerPhone('');
    setFormCourtId('');
    setFormStartHour('');
    setFormDuration('1');
    setFormWeekdays([]);
    setFormStartDate(new Date().toISOString().split('T')[0]);
    setFormInterval('1');
    setConflicts([]);
  };

  const toggleWeekday = (day) => {
    setFormWeekdays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const handleCreate = async () => {
    if (!formPlayerName.trim()) { toast.error('Enter player name'); return; }
    if (!formPlayerEmail.trim()) { toast.error('Enter player email'); return; }
    if (!formCourtId) { toast.error('Select a court'); return; }
    if (!formStartHour) { toast.error('Select start time'); return; }
    if (formWeekdays.length === 0) { toast.error('Select at least one day'); return; }
    if (formBookingType === 'coaching' && !formCoachId) { toast.error('Select a coach'); return; }

    setSubmitting(true);
    try {
      const res = await createRecurringBookingApi({
        player_name: formPlayerName.trim(),
        player_email: formPlayerEmail.trim(),
        player_phone: formPlayerPhone.trim() || null,
        court_id: formCourtId,
        start_hour: parseInt(formStartHour),
        duration_hours: parseInt(formDuration),
        weekdays: formWeekdays.sort(),
        start_date: formStartDate,
        repeat_interval: parseInt(formInterval),
        booking_type: formBookingType,
        coach_id: formBookingType === 'coaching' ? formCoachId : null,
      });

      if (res.data.conflicts?.length > 0) {
        setConflicts(res.data.conflicts);
        toast.warning(`Created with ${res.data.conflicts.length} conflicts — some slots already taken`);
      } else {
        toast.success(`Created! ${res.data.instances_created} bookings scheduled`);
        setCreateOpen(false);
        resetForm();
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create recurring booking');
    }
    setSubmitting(false);
  };

  const handlePauseToggle = async (s) => {
    try {
      await updateRecurringBookingApi(s.id, { is_paused: !s.is_paused });
      toast.success(s.is_paused ? 'Resumed' : 'Paused');
      loadData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleRegenerate = async (s) => {
    try {
      const res = await regenerateRecurringBookingApi(s.id);
      toast.success(`Regenerated ${res.data.instances_created} bookings`);
      loadData();
    } catch (err) {
      toast.error('Failed to regenerate');
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete recurring booking for ${s.player_name}? Unpaid future bookings will be cancelled.`)) return;
    try {
      await deleteRecurringBookingSeriesApi(s.id);
      toast.success('Recurring booking deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const getIntervalLabel = (interval) => {
    if (interval === 1) return 'Every week';
    if (interval === 2) return 'Every 2 weeks';
    return `Every ${interval} weeks`;
  };

  if (!hasPermission('create_booking')) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <p className="text-gray-500">You don't have permission to manage recurring bookings.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app-bg" data-testid="recurring-bookings-page">
      {/* Header */}
      <div className="bg-white border-b border-black/5 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="rb-back-btn"
              onClick={() => navigate('/operator')}
              className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <h1 className="font-heading text-lg font-semibold text-gray-900">Recurring Bookings</h1>
          </div>
          <Button
            data-testid="create-recurring-booking-btn"
            onClick={() => setCreateOpen(true)}
            className="bg-brand-teal hover:bg-brand-teal-hover text-white rounded-xl px-4 py-2 text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-1" /> New
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <div className="text-center text-gray-400 py-12">Loading...</div>
        ) : series.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            <Repeat className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No recurring bookings yet</p>
            <p className="text-sm">Create one for regular players</p>
          </div>
        ) : (
          series.map(s => (
            <div
              key={s.id}
              data-testid={`recurring-booking-${s.id}`}
              className={`bg-white rounded-3xl border border-black/5 p-5 transition-all ${s.is_paused ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-bold text-gray-900">{s.player_name}</h3>
                    {s.is_paused && <Badge variant="secondary" className="text-xs">Paused</Badge>}
                    {s.booking_type === 'coaching' && (
                      <Badge className="text-[10px] font-semibold gap-1 bg-brand-teal-tint text-brand-teal border-brand-teal/30" data-testid={`rb-badge-coaching-${s.id}`}>
                        <GraduationCap className="w-3 h-3" />
                        Coaching{s.coach_name ? ` · ${s.coach_name}` : ''}
                      </Badge>
                    )}
                    {s.booking_type === 'training' && (
                      <Badge className="text-[10px] font-semibold gap-1" style={{ backgroundColor: '#F7FBEA', color: '#6F8C00', border: '1px solid #D9E6B5' }} data-testid={`rb-badge-training-${s.id}`}>
                        <Target className="w-3 h-3" />
                        Training
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {formatHour(s.start_hour)}–{formatHour(s.start_hour + s.duration_hours)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {s.court_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" />
                      {s.player_email}
                    </span>
                    {s.player_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5" />
                        {s.player_phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Weekdays */}
              <div className="flex gap-1.5 mb-3">
                {WEEKDAY_NAMES.map((name, i) => (
                  <span
                    key={i}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium ${
                      s.weekdays.includes(i)
                        ? 'bg-brand-teal text-white'
                        : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {name}
                  </span>
                ))}
              </div>

              {/* Meta */}
              <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
                <span>{getIntervalLabel(s.repeat_interval)}</span>
                <span>·</span>
                <span>From {s.start_date}</span>
                <span>·</span>
                <span>No end date</span>
              </div>

              {/* Actions */}
              {hasPermission('block_court') && (
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`pause-rb-${s.id}`}
                    onClick={() => handlePauseToggle(s)}
                    className="rounded-lg text-xs"
                  >
                    {s.is_paused ? <Play className="w-3 h-3 mr-1" /> : <Pause className="w-3 h-3 mr-1" />}
                    {s.is_paused ? 'Resume' : 'Pause'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`regenerate-rb-${s.id}`}
                    onClick={() => handleRegenerate(s)}
                    className="rounded-lg text-xs"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`delete-rb-${s.id}`}
                    onClick={() => handleDelete(s)}
                    className="rounded-lg text-xs text-blocks border-blocks/30 hover:bg-blocks-tint"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Delete Series
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-recurring-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Recurring Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Booking Type */}
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">Booking Type</Label>
              <div className="grid grid-cols-3 gap-2" data-testid="rb-booking-type">
                {[
                  { id: 'court', label: 'Court', Icon: MapPin },
                  { id: 'coaching', label: 'Coaching', Icon: GraduationCap },
                  { id: 'training', label: 'Training', Icon: Target },
                ].map(({ id, label, Icon }) => {
                  const active = formBookingType === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      data-testid={`rb-type-${id}`}
                      onClick={() => { setFormBookingType(id); if (id !== 'coaching') setFormCoachId(''); }}
                      className={`flex flex-col items-center justify-center gap-1 py-3 rounded-xl text-xs font-semibold border transition-all ${
                        active
                          ? 'bg-brand-teal text-white border-brand-teal shadow-md'
                          : 'bg-white text-gray-600 border-black/5 hover:border-brand-teal/30'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  );
                })}
              </div>
              {formBookingType === 'coaching' && (
                <p className="text-[11px] text-gray-500 mt-1.5">
                  Court-only rate: ₱250/hr (6 AM–6 PM), ₱350/hr otherwise.
                </p>
              )}
              {formBookingType === 'training' && (
                <p className="text-[11px] text-gray-500 mt-1.5">
                  Training package: court + ball machine + 80 balls + 2 pickers. Reserves the ball machine — no other court can book it at the same time.
                </p>
              )}
            </div>

            {/* Coach (Coaching only) */}
            {formBookingType === 'coaching' && (
              <div>
                <Label className="text-sm text-gray-600">Coach</Label>
                <Select value={formCoachId} onValueChange={setFormCoachId}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="rb-coach">
                    <SelectValue placeholder="Select coach" />
                  </SelectTrigger>
                  <SelectContent>
                    {coaches.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-gray-400">
                        No active coaches. Create one from Add → Coaching Session.
                      </div>
                    ) : (
                      coaches.map(c => (
                        <SelectItem key={c.id} value={c.id}>Coach {c.coach_name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Player Name */}
            <div>
              <Label className="text-sm text-gray-600">Player Name</Label>
              <Input
                data-testid="rb-player-name"
                value={formPlayerName}
                onChange={(e) => setFormPlayerName(e.target.value)}
                placeholder="e.g. John Santos"
                className="rounded-xl mt-1"
              />
            </div>

            {/* Player Email */}
            <div>
              <Label className="text-sm text-gray-600">Player Email</Label>
              <Input
                data-testid="rb-player-email"
                type="email"
                value={formPlayerEmail}
                onChange={(e) => setFormPlayerEmail(e.target.value)}
                placeholder="john@email.com"
                className="rounded-xl mt-1"
              />
            </div>

            {/* Player Phone */}
            <div>
              <Label className="text-sm text-gray-600">Phone (optional)</Label>
              <Input
                data-testid="rb-player-phone"
                value={formPlayerPhone}
                onChange={(e) => setFormPlayerPhone(e.target.value)}
                placeholder="+63 9XX XXX XXXX"
                className="rounded-xl mt-1"
              />
            </div>

            {/* Court */}
            <div>
              <Label className="text-sm text-gray-600">Court</Label>
              <Select value={formCourtId} onValueChange={setFormCourtId}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="rb-court">
                  <SelectValue placeholder="Select court" />
                </SelectTrigger>
                <SelectContent>
                  {courts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-gray-600">Start Time</Label>
                <Select value={formStartHour} onValueChange={setFormStartHour}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="rb-start-hour">
                    <SelectValue placeholder="Start" />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map(h => (
                      <SelectItem key={h} value={String(h)}>{formatHour(h)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Duration</Label>
                <Select value={formDuration} onValueChange={setFormDuration}>
                  <SelectTrigger className="rounded-xl mt-1" data-testid="rb-duration">
                    <SelectValue placeholder="Duration" />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map(d => (
                      <SelectItem key={d} value={String(d)}>{d} hour{d > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Weekdays */}
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">Repeat on</Label>
              <div className="flex gap-1.5">
                {WEEKDAY_NAMES.map((name, i) => (
                  <button
                    key={i}
                    data-testid={`rb-day-${i}`}
                    onClick={() => toggleWeekday(i)}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                      formWeekdays.includes(i)
                        ? 'bg-brand-teal text-white shadow-md'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>

            {/* Interval */}
            <div>
              <Label className="text-sm text-gray-600">Repeat every</Label>
              <Select value={formInterval} onValueChange={setFormInterval}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="rb-interval">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Every week</SelectItem>
                  <SelectItem value="2">Every 2 weeks</SelectItem>
                  <SelectItem value="3">Every 3 weeks</SelectItem>
                  <SelectItem value="4">Every 4 weeks</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div>
              <Label className="text-sm text-gray-600">Start Date</Label>
              <Input
                data-testid="rb-start-date"
                type="date"
                value={formStartDate}
                onChange={(e) => setFormStartDate(e.target.value)}
                className="rounded-xl mt-1"
              />
            </div>

            {/* Conflicts Warning */}
            {conflicts.length > 0 && (
              <div className="bg-status-held-bg rounded-xl p-3 text-sm text-status-held" data-testid="rb-conflicts">
                <p className="font-medium mb-1">Some slots had conflicts:</p>
                <ul className="text-xs space-y-0.5">
                  {conflicts.slice(0, 5).map((c, i) => (
                    <li key={i}>{c.date} at {c.hours} on {c.court_name}</li>
                  ))}
                  {conflicts.length > 5 && <li>...and {conflicts.length - 5} more</li>}
                </ul>
              </div>
            )}

            {/* Submit */}
            <Button
              data-testid="create-recurring-booking-submit"
              onClick={handleCreate}
              disabled={submitting}
              className="w-full bg-brand-teal hover:bg-brand-teal-hover text-white rounded-xl py-5 font-semibold"
            >
              {submitting ? 'Creating...' : 'Create Recurring Booking'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
